// The Inventory list's reads answered over the lots synced to the phone (BL-2430, ADR 066 —
// Offline Mode § 3): `GET /api/inventory`, its counts and its filter options, computed from the
// cached rows with the server's own param names and status map. Pure: no I/O, no clock.
import type { LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import { SALES_REP_FILTER_HOUSE } from '@auctogon/core-admin/services/sales-rep';
import { TAXABLE_LOT_STATUSES, TAXABLE_NO_RATE_FILTER_VALUE } from '@auctogon/core-admin/schemas/lot-tax';
import {
  INVOICE_FILTER_NOT_INVOICED,
  TITLE_MAILING_STATUS_FILTER_NOT_SET,
  TITLE_STATUS_FILTER_NO_STATUS,
  TITLE_STATUS_FILTER_NOT_TITLED,
} from '@auctogon/core-admin/item-form';
import type { InventoryListPayload } from '@auctogon/core-admin/schemas/responses/inventory-list';
import type { InventoryCountsPayload } from '@auctogon/core-admin/schemas/responses/inventory-counts';
import type { InventoryFilterOptionsPayload } from '@auctogon/core-admin/schemas/responses/inventory-filter-options';
import type { AuctionListItem } from '@auctogon/core-admin/schemas/responses/auction';
import type { AuctionPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import type { SalesRepDto } from '@auctogon/core-admin/schemas/responses/sales-rep';
import type { TaxRateDto } from '@auctogon/core-admin/schemas/responses/tax-rate';
import { LOT_ELIGIBLE_AUCTION_STATUSES } from '@auctogon/core-admin/services/auctions';
import { deriveCustomerIdentityLines } from '@auctogon/core/isomorphic/customers/customer-identity-lines';
import { formatSalesRepName } from '@auctogon/core/sales-rep';
import type { OfflineDeltaLotDto } from '@auctogon/core-admin/schemas/responses/offline-sync';

import { phasesForStatusKey } from '../constants/inventory-status-filter';
import {
  DEFAULT_LIST_PAGE_SIZE,
  normalizeInventoryFilterList,
  normalizeListSearchTerm,
  parseListInstant,
  parseListLimit,
  parseListOffset,
} from '../lists/list-query-params';

/**
 * The filters the offline list hides, because the cached row can't decide them (operator
 * decision Q8 on BL-2343): no ring is cached (BL-2261 took `ringId` off the lot payloads), and
 * home lots are never synced, so Assignment would always read "assigned".
 */
export const OFFLINE_HIDDEN_LOT_FILTERS = ['ring', 'assignment'] as const;

/** The list route's page size and cap (`GET /api/inventory`). */
const LIST_LIMITS = { defaultLimit: DEFAULT_LIST_PAGE_SIZE, maxLimit: 200 };

type CachedLot = OfflineDeltaLotDto;

/** The lot's effective sales rep: its own override, else its seller's (the server's COALESCE). */
function effectiveRepId(lot: CachedLot): string | null {
  return lot.detail.salesRepId ?? lot.detail.sellerSalesRepId ?? null;
}

function includesText(value: string | null | undefined, needle: string): boolean {
  return value != null && value.toLowerCase().includes(needle);
}

/** The server's eight search arms (ADR 059 — Search Spine § 2.1), over the cached row. */
function matchesSearch(lot: CachedLot, search: string): boolean {
  const needle = search.toLowerCase();
  const { row, detail } = lot;
  const lotNumber = detail.lotNumber == null ? null : `${detail.lotNumber}${detail.lotNumberSuffix}`;
  return (
    includesText(row.inventoryNumber, needle) ||
    includesText(row.name, needle) ||
    includesText(lotNumber, needle) ||
    includesText(row.seller?.accountName, needle) ||
    includesText(row.seller?.companyName, needle) ||
    includesText(row.seller?.fullName, needle) ||
    includesText(row.auctionName, needle) ||
    includesText(row.effectiveSalesRepName === 'House' ? null : row.effectiveSalesRepName, needle)
  );
}

function matchesTitleStatus(lot: CachedLot, values: string[]): boolean {
  const titled = lot.row.isTitledItem === true;
  return values.some((v) => {
    if (v === TITLE_STATUS_FILTER_NOT_TITLED) return !titled;
    if (v === TITLE_STATUS_FILTER_NO_STATUS) return titled && lot.row.titleStatus == null;
    return titled && lot.row.titleStatus === v;
  });
}

function matchesTitleMailing(lot: CachedLot, values: string[]): boolean {
  if (lot.row.isTitledItem !== true) return false;
  return values.some((v) => (v === TITLE_MAILING_STATUS_FILTER_NOT_SET ? lot.row.titleMailingStatus == null : lot.row.titleMailingStatus === v));
}

function matchesTaxRate(lot: CachedLot, values: string[]): boolean {
  return values.some((v) =>
    v === TAXABLE_NO_RATE_FILTER_VALUE
      ? TAXABLE_LOT_STATUSES.includes(lot.row.taxStatus) && lot.row.taxRate == null
      : lot.row.taxRate?.id === v,
  );
}

function matchesRep(lot: CachedLot, values: string[]): boolean {
  const rep = effectiveRepId(lot);
  return values.some((v) => (v === SALES_REP_FILTER_HOUSE ? rep == null : rep === v));
}

function inWindow(updatedAt: string, start: string | null, end: string | null): boolean {
  const at = Date.parse(updatedAt);
  if (start != null && at < Date.parse(start)) return false;
  if (end != null && at > Date.parse(end)) return false;
  return true;
}

/** The request's facets, parsed once per read rather than once per cached lot. */
interface ParsedFacets {
  lists: Record<string, string[] | null>;
  search: string | null;
  start: string | null;
  end: string | null;
  phases: Set<LotPhaseKind> | null;
  wantsDeletedOnly: boolean;
}

const FACET_KEYS = ['auctionId', 'sellerId', 'categoryId', 'assignment', 'salesRepId', 'taxStatus', 'taxRateId', 'titleStatus', 'titleMailingStatus', 'invoice'] as const;

function parseFacets(query: URLSearchParams): ParsedFacets {
  const lists = Object.fromEntries(FACET_KEYS.map((key) => [key, normalizeInventoryFilterList(query.get(key))]));
  const statuses = normalizeInventoryFilterList(query.get('status'));
  const phases = statuses == null ? null : new Set(statuses.flatMap((s) => phasesForStatusKey(s) ?? []));
  return {
    lists,
    search: normalizeListSearchTerm(query.get('search')),
    start: parseListInstant(query.get('lastModifiedStart')),
    end: parseListInstant(query.get('lastModifiedEnd')),
    phases,
    wantsDeletedOnly: statuses?.includes('deleted') ?? false,
  };
}

/** Every facet but status, as the counts read them ("how many would show for this chip"). */
function matchesFacets(lot: CachedLot, facets: ParsedFacets): boolean {
  const { row } = lot;
  const list = (key: (typeof FACET_KEYS)[number]) => facets.lists[key];
  const checks: Array<[string[] | null, (values: string[]) => boolean]> = [
    [list('auctionId'), (v) => row.auctionId != null && v.includes(row.auctionId)],
    [list('sellerId'), (v) => row.sellerId != null && v.includes(row.sellerId)],
    [list('categoryId'), (v) => row.categoryId != null && v.includes(row.categoryId)],
    [list('assignment'), (v) => v.includes('assigned')],
    [list('salesRepId'), (v) => matchesRep(lot, v)],
    [list('taxStatus'), (v) => v.includes(row.taxStatus)],
    [list('taxRateId'), (v) => matchesTaxRate(lot, v)],
    [list('titleStatus'), (v) => matchesTitleStatus(lot, v)],
    [list('titleMailingStatus'), (v) => matchesTitleMailing(lot, v)],
    [list('invoice'), (v) => v.includes(INVOICE_FILTER_NOT_INVOICED) && row.lotPhaseKind === 'sold' && row.afterSale?.invoice == null],
  ];
  if (checks.some(([values, test]) => values != null && !test(values))) return false;
  if (facets.search != null && !matchesSearch(lot, facets.search)) return false;
  return inWindow(row.updatedAt, facets.start, facets.end);
}

/** The status facet: phase keys through the server's map. `deleted` is never cached. */
function matchesStatus(lot: CachedLot, facets: ParsedFacets): boolean {
  if (facets.phases == null) return true;
  // As the server: a retired or unknown key does not narrow (BUG-1842), and `deleted` alone
  // matches nothing here, since deleted lots are never synced.
  if (facets.phases.size === 0) return !facets.wantsDeletedOnly;
  return lot.row.lotPhaseKind != null && facets.phases.has(lot.row.lotPhaseKind);
}

type SortValue = string | number | null;

function moneyValue(wire: { amount: string } | null | undefined): number | null {
  return wire == null ? null : Number(wire.amount);
}

/** Each supported column's sort value. Unknown columns (Rep among them) fall to the default. */
function sortValueFor(column: string): ((lot: CachedLot) => SortValue) | null {
  const custom = /^customField_(\d+)$/.exec(column);
  if (custom) return (lot) => (lot.row.customFieldValues as Record<string, string | null> | undefined)?.[custom[1]] ?? null;
  const columns: Record<string, (lot: CachedLot) => SortValue> = {
    inventoryNumber: (lot) => lot.row.inventoryNumber,
    created: (lot) => lot.row.createdAt,
    createdAt: (lot) => lot.row.createdAt,
    name: (lot) => lot.row.name,
    item: (lot) => lot.row.name,
    startPrice: (lot) => moneyValue(lot.row.startPrice),
    reserve: (lot) => moneyValue(lot.row.reserve),
    close: (lot) => lot.row.closeTime ?? null,
    category: (lot) => lot.row.categoryId ?? null,
    seller: (lot) => lot.row.seller?.accountName ?? null,
    buyer: (lot) => lot.row.afterSale?.buyer?.accountName ?? null,
    status: (lot) => lot.row.lotPhaseKind ?? null,
    hammer: (lot) => moneyValue(lot.row.hammerAdjustment?.buyerPays ?? lot.row.hammerPrice),
    taxStatus: (lot) => lot.row.taxStatus,
    taxRate: (lot) => lot.row.taxRate?.name ?? null,
    titleStatus: (lot) => lot.row.titleStatus ?? null,
    titleMailingStatus: (lot) => lot.row.titleMailingStatus ?? null,
    auctionLot: (lot) => lot.row.auctionName ?? null,
  };
  return Object.hasOwn(columns, column) ? columns[column] : null;
}

/** Nulls last in both directions, as the server sorts its display columns (BL-2011). */
function compareValues(a: SortValue, b: SortValue, dir: 1 | -1): number {
  if (a == null || b == null) return a == null && b == null ? 0 : a == null ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * dir;
}

/** Lot number then its A–Z suffix: the offline Lots tab's order (operator decision Q3, BL-2430). */
function compareLotNumber(a: CachedLot, b: CachedLot, dir: 1 | -1): number {
  const byNumber = compareValues(a.detail.lotNumber, b.detail.lotNumber, dir);
  return byNumber !== 0 ? byNumber : compareValues(a.detail.lotNumberSuffix, b.detail.lotNumberSuffix, dir);
}

function sortLots(lots: CachedLot[], query: URLSearchParams): CachedLot[] {
  const column = query.get('sortColumn');
  const dir: 1 | -1 = query.get('sortDirection') === 'asc' ? 1 : -1;
  const value = column ? sortValueFor(column) : null;
  const tiebreak = (a: CachedLot, b: CachedLot) => a.row.lotId.localeCompare(b.row.lotId);
  let compare: (a: CachedLot, b: CachedLot) => number;
  if (column === 'lotNumber') compare = (a, b) => compareLotNumber(a, b, dir);
  else if (value) compare = (a, b) => compareValues(value(a), value(b), dir);
  // The server's default: lot sequence on one auction (offline, lot number), newest first elsewhere.
  else if (normalizeInventoryFilterList(query.get('auctionId'))?.length === 1) compare = (a, b) => compareLotNumber(a, b, 1);
  else compare = (a, b) => compareValues(a.row.createdAt, b.row.createdAt, -1);
  return [...lots].sort((a, b) => compare(a, b) || tiebreak(a, b));
}

/**
 * `GET /api/inventory` over the cached lots.
 *
 * @param input.lots - The synced lots.
 * @param input.query - The request's query string, as the online route reads it.
 * @returns The page and the filtered total.
 */
export function queryOfflineLots(input: { lots: CachedLot[]; query: URLSearchParams }): InventoryListPayload {
  const { lots, query } = input;
  const facets = parseFacets(query);
  const matched = sortLots(lots.filter((lot) => matchesFacets(lot, facets) && matchesStatus(lot, facets)), query);
  const limit = parseListLimit(query.get('limit'), LIST_LIMITS);
  const offset = parseListOffset(query.get('offset'));
  return { items: matched.slice(offset, offset + limit).map((lot) => lot.row), total: matched.length };
}

/** Each count chip's phase key in the counts payload. */
const COUNT_BUCKETS: Array<[keyof InventoryCountsPayload, string]> = [
  ['draft', 'draft'],
  ['ready', 'ready'],
  ['preBidding', 'pre-bidding'],
  ['openForBidding', 'open-for-bidding'],
  ['lastCall', 'last-call'],
  ['awaitingResolution', 'awaiting-resolution'],
  ['paused', 'paused'],
  ['sold', 'sold'],
  ['unsold', 'unsold'],
  ['withdrawn', 'withdrawn'],
];

/**
 * `GET /api/inventory/counts` over the cached lots: every facet but status, bucketed by phase.
 * `unassigned` and `deleted` are 0 offline (home lots and deleted lots are never synced).
 *
 * @param input.lots - The synced lots.
 * @param input.query - The request's query string.
 * @returns The bucket counts.
 */
export function countOfflineLots(input: { lots: CachedLot[]; query: URLSearchParams }): InventoryCountsPayload {
  const facets = parseFacets(input.query);
  const phases = input.lots.filter((lot) => matchesFacets(lot, facets)).map((lot) => lot.row.lotPhaseKind);
  const counts: InventoryCountsPayload = {
    unassigned: 0, draft: 0, ready: 0, preBidding: 0, openForBidding: 0, lastCall: 0,
    awaitingResolution: 0, paused: 0, sold: 0, unsold: 0, withdrawn: 0, deleted: 0, total: 0,
  };
  for (const [key, status] of COUNT_BUCKETS) {
    const bucket = new Set<LotPhaseKind>(phasesForStatusKey(status) ?? [status as LotPhaseKind]);
    counts[key] = phases.filter((p) => p != null && bucket.has(p)).length;
  }
  counts.total = COUNT_BUCKETS.reduce((sum, [key]) => sum + counts[key], 0);
  return counts;
}

/** One synced auction, as the offline filter options read it. */
export interface OfflineAuctionOptionSource {
  listRow: AuctionListItem;
  phaseKind: AuctionPhaseKind;
}

/**
 * Active reps as filter options, named and ordered as the online option lists name and order them
 * (last name, then first).
 *
 * @param reps - The cached sales reps.
 * @param keep - Which rep ids to offer, or all when omitted.
 * @returns The `{ id, name }` options.
 */
export function offlineSalesRepOptions(reps: SalesRepDto[], keep?: ReadonlySet<string>): Array<{ id: string; name: string }> {
  return reps
    .filter((rep) => rep.deletedAt == null && (keep == null || keep.has(rep.id)))
    .sort((a, b) => (a.lastName ?? '').localeCompare(b.lastName ?? '') || (a.firstName ?? '').localeCompare(b.firstName ?? ''))
    .map((rep) => ({ id: rep.id, name: formatSalesRepName(rep) ?? '' }));
}

/**
 * `GET /api/inventory/filter-options` over what the phone holds: the synced auctions, the sellers,
 * categories and effective reps present on the cached lots, and the cached tax rates. Rings are
 * never cached (`[]`; the Ring facet is hidden offline).
 *
 * `acceptsLots` reads the phase half of `auctionAcceptsLotsFromRow`; its "sale already finished"
 * half needs lot close times the header doesn't carry, and the write re-checks it on replay.
 *
 * @param input.lots - The synced lots.
 * @param input.auctions - The synced auctions.
 * @param input.salesReps - The cached sales reps, or none.
 * @param input.taxRates - The cached tax rates, or none.
 * @returns The options payload.
 */
export function offlineLotFilterOptions(input: {
  lots: CachedLot[];
  auctions: OfflineAuctionOptionSource[];
  salesReps: SalesRepDto[];
  taxRates: TaxRateDto[];
}): InventoryFilterOptionsPayload {
  const sellers = new Map<string, string | null>();
  const categories = new Set<string>();
  const repIds = new Set<string>();
  for (const lot of input.lots) {
    if (lot.row.sellerId != null) sellers.set(lot.row.sellerId, lot.row.seller ? deriveCustomerIdentityLines(lot.row.seller).label : null);
    if (lot.row.categoryId != null) categories.add(lot.row.categoryId);
    const rep = effectiveRepId(lot);
    if (rep != null) repIds.add(rep);
  }
  return {
    auctions: input.auctions.map(({ listRow, phaseKind }) => ({
      id: listRow.id,
      name: listRow.title,
      status: phaseKind,
      startDate: listRow.startDate,
      format: listRow.type,
      timeZone: listRow.timeZone,
      acceptsLots: LOT_ELIGIBLE_AUCTION_STATUSES.has(phaseKind),
    })),
    sellers: [...sellers].map(([id, name]) => ({ id, name })),
    categories: [...categories],
    rings: [],
    salesReps: offlineSalesRepOptions(input.salesReps, repIds),
    taxRates: input.taxRates.map((rate) => ({ id: rate.id, name: rate.name, archived: false })),
  };
}
